import React from 'react';
import { cloneDeep, get } from 'lodash';
import { Skeleton } from 'antd';
import { LESS_THAN_NUMBER } from '@utils/metrics';
import { INTERNAL_UNKNOWN } from '@utils/constants';
import {
  BM_DIMENTIONS_MAPPING,
  BM_SUPPORTED_ADVANCED_DIMENSIONS,
  BM_SUPPORTED_DIMENSIONS,
} from '@src/utils/kpi';
import { getMetricOvbShortName, getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import ValueCell from './ValueCell';
import Cell from './Cell';

const LOADING_STATUS = -10;
const ERROR_STATUS = -11;
const NA_STATUS = -1;
export const isCountryFilterApplied = (rule, dimension) => {
  let allFilter = rule.flat(Infinity);
  allFilter = allFilter.filter((filt) => filt.field === 'GEO_COUNTRY');
  return allFilter.length > 0 || dimension === 'GEO_COUNTRY';
};
export const isNonBenchmarkFilterApplied = (rule) => {
  const allFilter = rule.flat(Infinity);
  const supportedDimension = [...BM_SUPPORTED_DIMENSIONS, ...BM_SUPPORTED_ADVANCED_DIMENSIONS];
  const nonBenchmarkFilter = allFilter.filter(
    (filt) => !(supportedDimension.includes(filt.field) || supportedDimension.includes(filt.key)),
  );
  return nonBenchmarkFilter.length > 0;
};
const getBenchmarkColumns = (benchmarkData: any, columns: string[] = []) => {
  let benchmarkColumns: string[] = [];

  const data = get(benchmarkData, ['response', 'result', 0], {});
  benchmarkColumns = Object.getOwnPropertyNames(data);
  benchmarkColumns = benchmarkColumns.filter((column) => columns.includes(column));

  return benchmarkColumns;
};

export const benchmarkAndDimensionAndMetricSupported = ({
  dimension,
  supportedMetricsStore,
  selectedKpiMetric,
  benchmarkFlag,
  advancedBenchmarkFlag,
  allDimension,
}) => {
  const supportedMetrics = get(supportedMetricsStore, ['response', 'data', 'data'], []).includes(
    selectedKpiMetric,
  );
  if (advancedBenchmarkFlag) {
    return (
      (BM_SUPPORTED_DIMENSIONS.includes(dimension) ||
        BM_SUPPORTED_ADVANCED_DIMENSIONS.includes(dimension)) &&
      supportedMetrics
    );
  }
  if (benchmarkFlag) {
    return BM_SUPPORTED_DIMENSIONS.includes(dimension) && supportedMetrics;
  }
  return false;
};
export const benchmarkSupportedAndFilterApplied = (
  dimension,
  isBenchmarkAndDimensionAndMetricSupported,
  appliedFilter,
) => {
  const countryFilterApplied = isCountryFilterApplied(get(appliedFilter, 'rule', []), dimension);
  const nonBenchmarkFilterApplied = isNonBenchmarkFilterApplied(get(appliedFilter, 'rule', []));
  return (
    countryFilterApplied && isBenchmarkAndDimensionAndMetricSupported && !nonBenchmarkFilterApplied
  );
};

export const includesBenchmarkColumn = (selectedKpiMetric, columnLength) => {
  return {
    title: 'Benchmark',
    dataKey: `benchmark.${selectedKpiMetric}`,
    key: `benchmark.${selectedKpiMetric}`,
    metric: selectedKpiMetric,
    // if the column length is one, it has to be aligned to the right and occupy the whole width
    width: 270 / columnLength,
    align: 'right',
    sortable: true,
    cellRenderer: ({ cellData, column }) => {
      if (cellData === LOADING_STATUS) return <Skeleton active></Skeleton>;
      if (cellData === ERROR_STATUS || cellData === NA_STATUS || cellData < LESS_THAN_NUMBER)
        return <ValueCell cellData={'NA'} column={column} />;
      return <ValueCell cellData={cellData} column={column} />;
    },
  };
};

export const formatColumns = ({
  dimension,
  columns,
  unit,
  benchmarkData,
  selectedKpiMetric,
  isBenchmarkSupportedAndFilterApplied,
}) => {
  const columnConfig: any = [];

  // first column is the dimension value
  columnConfig.push({
    title: '',
    dataKey: 'name',
    key: dimension,
    width: 270,
    cellRenderer: ({ cellData, column, rowData }) => (
      <Cell cellData={cellData} column={column} rowData={rowData} />
    ),
  });

  const tableColumns = columns.map((m) => {
    const percentageMetric = getPercentageMetricByKey({ metricKey: m });
    if (unit === '%' && percentageMetric) {
      return percentageMetric;
    }
    return m;
  });

  const benchmarkColumns = getBenchmarkColumns(benchmarkData, tableColumns);

  const columnLength =
    tableColumns.length + (benchmarkColumns.length || isBenchmarkSupportedAndFilterApplied) || 1;
  // remaining columns are the metric values
  tableColumns.forEach((metric) => {
    const shortName = getMetricOvbShortName({ metricKey: metric });
    columnConfig.push({
      title: shortName,
      dataKey: metric,
      key: metric,
      metric,
      // if the column length is one, it has to be aligned to the right and occupy the whole width
      width: 270 / columnLength,
      align: 'right',
      sortable: true,
      cellRenderer: ({ cellData, column }) => <ValueCell cellData={cellData} column={column} />,
    });
  });

  if (isBenchmarkSupportedAndFilterApplied) {
    const benchmarkColumn = includesBenchmarkColumn(selectedKpiMetric, columnLength - 1);
    columnConfig.push(benchmarkColumn);
  }
  return columnConfig;
};

// format data for table
export function formatData(
  { isFetching = true, response = {}, errorMsg = '' },
  benchmark,
  dimension,
  columns,
  unit,
  selectedKpiMetric,
) {
  const data = get(response, ['data', 0, 'results'], []);

  const benchmarkResponse = benchmark.response ? benchmark.response : {};

  const benchmarkData = get(benchmarkResponse, 'result');

  const convertedDimension = BM_DIMENTIONS_MAPPING[dimension];

  if (benchmarkData && data.length > 0) {
    benchmarkData.forEach((bm) => {
      const filteredMetric = data.filter((metric) => {
        if (metric[convertedDimension] && bm[convertedDimension] === metric[convertedDimension]) {
          return metric;
        }
      });
      if (bm[selectedKpiMetric] === 'NA') bm[selectedKpiMetric] = NA_STATUS;
      if (filteredMetric.length > 0) {
        filteredMetric[0].benchmark = bm;
      }
    });
  }

  let benchmarkColumns;
  let tableBenchmarkColumns = [];
  if (benchmarkData) {
    const benchmarkDataResult = get(benchmarkData, 0) || {};
    benchmarkColumns = Object.getOwnPropertyNames(benchmarkDataResult); // getBenchmarkColumns(benchmarkData, tableColumns);
    tableBenchmarkColumns = benchmarkColumns.map((m) => {
      const percentageMetric = getPercentageMetricByKey({ metricKey: m });
      if (unit === '%' && percentageMetric) {
        return percentageMetric;
      }
      return m;
    });
  }
  // move unknown rows to bottom
  const filteredData: any = [];
  const unknownRows: any = [];
  cloneDeep(data).forEach((r) => {
    const value = r.value.join('');
    // If benchmark values not present then put a dummy values, so that sorting wont be impacted.
    if (!r.benchmark) {
      r.benchmark = {};
      if (tableBenchmarkColumns.length) {
        tableBenchmarkColumns.forEach((m) => {
          r.benchmark[m] = NA_STATUS;
        });
      } else {
        r.benchmark[selectedKpiMetric] = NA_STATUS;
      }
    }
    // Put placeholder for each row
    if (benchmark.isFetching) {
      r.benchmark[selectedKpiMetric] = LOADING_STATUS; // loading icon
    }
    if (!benchmark.isFetching && benchmark.errorMsg) {
      r.benchmark[selectedKpiMetric] = ERROR_STATUS; // error
    }
    if (value !== INTERNAL_UNKNOWN) {
      filteredData.push(r);
    } else {
      unknownRows.push(r);
    }
  });

  const isError = errorMsg.length !== 0;
  const isEmpty = !isError && data.length === 0;
  return {
    data: [...filteredData, ...unknownRows],
    isFetching,
    isError,
    isEmpty,
  };
}
